// Funnel: ordered stages on an ordinal ramp, step conversion vs configured benchmark, with the
// source of each stage (GA4 primary, Shopify checkouts filling gaps) stated.
import { formatValue } from '../../lib/format';
import { StatusBadge } from '../../ui/Badges';
import type { RegistryEntry, WidgetComponentProps } from '../types';

const RAMP = ['var(--seq-300)', 'var(--seq-400)', 'var(--seq-500)', 'var(--seq-600)', 'var(--seq-700)'];

function Funnel({ data }: WidgetComponentProps) {
  const stages: any[] = data.stages ?? [];
  const top = Math.max(1, ...stages.map((s) => s.value ?? 0));
  return (
    <div className="funnel">
      {stages.map((s, i) => (
        <div className="funnel-row" key={s.id}>
          <div>
            <div style={{ fontWeight: 600 }}>{s.label}</div>
            <div className="muted" style={{ fontSize: 11.5 }}>{s.source}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <div className="funnel-bar" style={{ width: `${s.value === null ? 0 : Math.max(0.4, (s.value / top) * 100)}%`, background: RAMP[Math.min(i, RAMP.length - 1)] }} />
            <span className="tabular" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatValue(s.value, 'integer', { compact: true })}</span>
          </div>
          <div className="funnel-meta">
            {s.rate !== null && s.rate !== undefined ? (
              <>
                <b className="tabular">{formatValue(s.rate, 'percent')}</b>
                {s.benchmark && <span className="muted">bench {formatValue(s.benchmark.min, 'percent', { compact: true })}–{formatValue(s.benchmark.max, 'percent', { compact: true })}</span>}
                {s.status && <StatusBadge status={s.status} />}
              </>
            ) : (
              <span className="muted">{i === 0 ? 'Entry' : 'Rate not comparable'}</span>
            )}
          </div>
        </div>
      ))}
      {data.overall && (
        <div className="funnel-row" style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <div style={{ fontWeight: 600 }}>{data.overall.label}</div>
          <div />
          <div className="funnel-meta">
            <b className="tabular">{formatValue(data.overall.rate, 'percent')}</b>
            {data.overall.benchmark && <span className="muted">bench {formatValue(data.overall.benchmark.min, 'percent', { compact: true })}–{formatValue(data.overall.benchmark.max, 'percent', { compact: true })}</span>}
            {data.overall.status && <StatusBadge status={data.overall.status} />}
          </div>
        </div>
      )}
      {data.note && <div className="hint">{data.note}</div>}
    </div>
  );
}

export const funnel: RegistryEntry = {
  component: Funnel,
  scroll: true,
  isEmpty: (d) => !d?.stages?.length,
  toTable: (d) => ({
    columns: [
      { key: 'label', label: 'Stage' },
      { key: 'value', label: 'Count', format: 'integer', numeric: true },
      { key: 'rate', label: 'Step rate', format: 'percent', numeric: true },
      { key: 'status', label: 'Vs benchmark', format: 'status' },
      { key: 'source', label: 'Source' },
    ],
    rows: d?.stages ?? [],
  }),
};
